import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import Modal from 'LuxKit/Modal/Modal'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import AccommodationAmenitiesAndPoliciesContent from './AccommodationAmenitiesAndPoliciesContent'
import AccommodationRoomDetailsList from './AccommodationRoomDetailsList'
import { mapRoomPackage } from './roomPackageUtils'
import RoomAmenitiesGallery from './RoomAmenitiesGallery'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const ModalGrid = styled.div`
  display: flex;
  gap: ${rem(32)};
  align-items: stretch;
  width: 100%;
  min-width: 0;
  flex-wrap: nowrap;
  max-height: 70vh;
`

const GalleryColumn = styled.div`
  flex: 0 0 calc(50% - ${rem(16)});
  max-width: calc(50% - ${rem(16)});
  min-width: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
`

const ContentColumn = styled.div`
  flex: 0 0 calc(50% - ${rem(16)});
  max-width: calc(50% - ${rem(16)});
  min-width: 0;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
`

const ContentScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`

const StickyFooter = styled.div`
  flex-shrink: 0;
  padding-top: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  margin-top: ${rem(16)};
`

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  checkIn?: string
  flights?: boolean
  onClose?: () => void
  initialImageIndex?: number
}

function AccommodationAmenitiesAndPoliciesModal(props: Props) {
  const { roomPackage, checkIn, flights = false, onClose, initialImageIndex = 0 } = props
  const offerPageDispatch = useOfferPageDispatch()
  const isDesktop = useScreenSizeUp('desktop')
  const gridGalleryExperiment = useOptimizelyExperiment(
    OptimizelyExperiments.croOfferGalleryGrid,
    isDesktop,
  )
  const shouldShowGridGalleryVariant = getOptimizelyEnabledDecision(gridGalleryExperiment ?? null)
  const intl = useIntl()
  const packageName = useMemo<string>(() => {
    return mapRoomPackage(
      roomPackage,
      ({ name }) => name,
      ({ roomType: { name } }) => name,
      ({ name }) => name,
    )
  }, [roomPackage])

  const roomImages = useMemo<Array<App.Image>>(() => {
    return mapRoomPackage(
      roomPackage,
      ({ images }) => images ?? EmptyArray,
      ({ roomType: { images } }) => images ?? EmptyArray,
      ({ images }) => images ?? EmptyArray,
    )
  }, [roomPackage])

  const shouldUseGalleryVariant = shouldShowGridGalleryVariant && isDesktop

  const handleCheckAvailability = useCallback(() => {
    onClose?.()
    offerPageDispatch({
      type: OfferPageActions.checkAvailability,
      flights,
      filter: 'checkin',
    })
  }, [offerPageDispatch, flights, onClose])

  const showCheckAvailability = !checkIn
  const showViewPackageOptions = !!checkIn
  const modalTitle = intl.formatMessage({
    id: 'qKBeeL8DwW',
    defaultMessage: 'Room amenities & policies',
    description: 'Title for the room amenities and policies modal.',
  })

  const roomDetailsHeading = intl.formatMessage({
    id: 'w1842pLgHS',
    defaultMessage: 'Room Details',
    description: 'Heading for the room details section inside the room amenities & policies modal.',
  })

  return <Modal
    title={modalTitle}
    subtitle={packageName}
    size={shouldUseGalleryVariant ? 'XL' : undefined}
  >
    {shouldUseGalleryVariant && (
      <ModalGrid>
        <GalleryColumn>
          <RoomAmenitiesGallery images={roomImages} initialIndex={initialImageIndex} />
        </GalleryColumn>
        <ContentColumn>
          <ContentScrollArea>
            <Group direction="vertical" gap={20}>
              {roomPackage.description && <CSSBreakpoint max="mobile"><BodyText variant="medium">{roomPackage.description}</BodyText></CSSBreakpoint>}
              <Group direction="vertical" gap={12}>
                <Heading variant="heading6">{roomDetailsHeading}</Heading>
                <AccommodationRoomDetailsList roomPackage={roomPackage} shouldShowAmenitiesAndPoliciesCta={false} />
              </Group>
              <AccommodationAmenitiesAndPoliciesContent roomPackage={roomPackage} />
            </Group>
          </ContentScrollArea>
          {showCheckAvailability && (
            <StickyFooter>
              <TextButton
                kind="primary"
                size="large"
                fit="full-width"
                onClick={handleCheckAvailability}
              >
                Check availability
              </TextButton>
            </StickyFooter>
          )}
          {showViewPackageOptions && (
            <StickyFooter>
              <TextButton
                kind="primary"
                size="large"
                fit="full-width"
                onClick={onClose}
              >
                View package options
              </TextButton>
            </StickyFooter>
          )}
        </ContentColumn>
      </ModalGrid>
    )}
    {!shouldUseGalleryVariant && (
      <Group direction="vertical" gap={20}>
        {roomPackage.description && <CSSBreakpoint max="mobile"><BodyText variant="medium">{roomPackage.description}</BodyText></CSSBreakpoint>}
        <Group direction="vertical" gap={12}>
          <Heading variant="heading6">{roomDetailsHeading}</Heading>
          <AccommodationRoomDetailsList roomPackage={roomPackage} shouldShowAmenitiesAndPoliciesCta={false} />
        </Group>
        <AccommodationAmenitiesAndPoliciesContent roomPackage={roomPackage} />
      </Group>
    )}
  </Modal>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  checkIn: offerPageState.checkInDate,
  flights: offerPageState.flights,
}), AccommodationAmenitiesAndPoliciesModal)
